/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-08-22 19:10:11
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import { SubmissionError } from 'redux-form';

import { store } from 'src/stores/store';
import {
  FILTER_TREE_DEFAULT_OPTION_TYPES,
  FILTER_OPTION_DEFAULT_VALUES,
  FILTER_TYPE,
  FAKED_PRODUCT_COUNT
} from 'src/constants/filter-tree';
import { capitalizeFirstLetter, slugify, prettyText } from '../index';
import percentSaleHelper from 'src/helpers/filter-tree-option/percent-sale-helper';
import reviewRatingsHelper from 'src/helpers/filter-tree-option/review-ratings-helper';
import stockStatusHelper from './stock-status-helper';
import weightHelper from './weight-helper';
import rangeSliderHelper from './range-slider-helper';
import variantsRangeSliderHelper from './variants-range-slider-helper';
import optionHelper from './option-helper';
import variantsPriceHelper from './variants-price-helper';
import priceHelper from './price-helper';
import tagHelper from './tag-helper';
import multiLevelTagHelper from './multi-level-tag-helper';
import productTypeHelper from './product-type-helper';
import vendorHelper from './vendor-helper';
import locationHelper from './location-helper';
import collectionHelper from './collection-helper';
import metafieldHelper from './metafield-helper';
import productCategoryHelper from './product-category-helper';

/**
 * Build initial data for option modal from filterOption
 *
 * @param  {Object} filterOption = {} if add new filter option, != {} if edit
 *
 * @return {Object} initialValue
 */
export const buildInitialFilterOptionValue = (initialFilterOption: Object) => {
  // Initial value form with new option
  // <!> Need to clone before using because the constant may be updated in "long-lived" process
  let initialValue = _.cloneDeep(FILTER_OPTION_DEFAULT_VALUES);
  const filterOption = _.cloneDeep(initialFilterOption);

  // Initial value for range slider
  const currentState = store.getState();
  let customRangeSliders = _.get(currentState, 'filterData.filterData.customRangeSliders', []);
  if (!_.isEmpty(customRangeSliders)) {
    customRangeSliders.map(item => {
      initialValue = {
        ...initialValue,
        // Range slider
        [`rangeSlider${item}SliderRange`]: 4,
        [`rangeSlider${item}SliderStep`]: 1,
        [`rangeSlider${item}SliderDelimiter`]: null,
        [`rangeSlider${item}HideTextBoxes`]: false
      };
      return '';
    });
  }

  let numberOfVariantsRangeSliders = _.get(
    currentState,
    `oauth.tenantApp.numberOfVariantsRangeSliders`,
    1
  );
  // Initial value for Variants Range Slider
  for (let i = 1; i <= numberOfVariantsRangeSliders; i++) {
    initialValue = {
      ...initialValue,
      // Range slider
      [`variantsRangeSlider${i}SliderRange`]: 4,
      [`variantsRangeSlider${i}SliderStep`]: 1,
      [`variantsRangeSlider${i}SliderDelimiter`]: null,
      [`variantsRangeSlider${i}HideTextBoxes`]: false
    };
  }

  //Initial value form with edit option
  if (!_.isEmpty(filterOption)) {
    // Shared field
    const {
      filterType,
      label,
      isCollapseMobile,
      isCollapsePC,
      tooltip,
      displayAllValuesInUppercaseForm,
      showSearchBoxFilterMobile,
      showMoreType,
      showSearchBoxFilterPC,
      sortManualValues,
      sortType,
      filterOptionId,
      position,
      status
    } = filterOption;

    initialValue = {
      ...initialValue,
      // General
      optionType: filterType,
      optionLabel: label,
      // Advance
      isCollapseMobile,
      isCollapsePC,
      displayTooltip: tooltip ? true : false,
      displayAllValuesInUppercaseForm,
      showSearchBoxFilterMobile,
      showSearchBoxFilterPC,
      showMoreType,
      sortManualValues,
      sortType,
      tooltip,
      //hidden value
      filterOptionId: filterOptionId,
      position,
      status
    };

    // Specific field for per option type
    const type =
      filterType && _.startsWith(filterType, FILTER_TYPE.OPTION)
        ? FILTER_TYPE.OPTION
        : filterType && _.startsWith(filterType, FILTER_TYPE.METAFIELD)
        ? FILTER_TYPE.METAFIELD
        : filterType && _.startsWith(filterType, FILTER_TYPE.RANGE_SLIDER)
        ? FILTER_TYPE.RANGE_SLIDER
        : filterType && _.startsWith(filterType, FILTER_TYPE.VARIANTS_RANGE_SLIDER)
        ? FILTER_TYPE.VARIANTS_RANGE_SLIDER
        : filterType;
    switch (type) {
      // Collection option
      case FILTER_TYPE.COLLECTION: {
        initialValue = collectionHelper.initialValues(filterOption, initialValue);
        break;
      }

      // Vendor option
      case FILTER_TYPE.VENDOR: {
        initialValue = vendorHelper.initialValues(filterOption, initialValue);
        break;
      }

      // Product type option
      case FILTER_TYPE.PRODUCT_TYPE: {
        initialValue = productTypeHelper.initialValues(filterOption, initialValue);
        break;
      }

      // Product category option
      case FILTER_TYPE.PRODUCT_CATEGORY: {
        initialValue = productCategoryHelper.initialValues(filterOption, initialValue);
        break;
      }

      // Location
      case FILTER_TYPE.LOCATION: {
        initialValue = locationHelper.initialValues(filterOption, initialValue);
        break;
      }

      // Tag option
      case FILTER_TYPE.TAG: {
        initialValue = tagHelper.initialValues(filterOption, initialValue);
        break;
      }

      // Multi-level tag option
      case FILTER_TYPE.MULTI_LEVEL_TAG: {
        initialValue = multiLevelTagHelper.initialValues(filterOption, initialValue);
        break;
      }

      // Price option
      case FILTER_TYPE.PRICE: {
        initialValue = priceHelper.initialValues(filterOption, initialValue);
        break;
      }

      // Variant's price option
      case FILTER_TYPE.VARIANTS_PRICE: {
        initialValue = variantsPriceHelper.initialValues(filterOption, initialValue);
        break;
      }

      // Perent sale
      case FILTER_TYPE.PERCENT_SALE: {
        initialValue = percentSaleHelper.initialValues(filterOption, initialValue);
        break;
      }

      // Custom option
      case FILTER_TYPE.OPTION: {
        initialValue = optionHelper.initialValues(filterOption, initialValue);
        break;
      }

      // Custom option
      case FILTER_TYPE.METAFIELD: {
        initialValue = metafieldHelper.initialValues(filterOption, initialValue);
        break;
      }

      // Review ratings
      case FILTER_TYPE.REVIEW_RATINGS: {
        initialValue = reviewRatingsHelper.initialValues(filterOption, initialValue);
        break;
      }

      // Stock status
      case FILTER_TYPE.STOCK_STATUS:
        initialValue = stockStatusHelper.initialValues(filterOption, initialValue);
        break;

      // Weight
      case FILTER_TYPE.WEIGHT:
        initialValue = weightHelper.initialValues(filterOption, initialValue);
        break;

      // Range
      case FILTER_TYPE.RANGE_SLIDER:
        initialValue = rangeSliderHelper.initialValues(filterOption, initialValue);
        break;

      // Variants Range Slider
      case FILTER_TYPE.VARIANTS_RANGE_SLIDER:
        initialValue = variantsRangeSliderHelper.initialValues(filterOption, initialValue);
        break;

      default:
        break;
    }
  }
  return initialValue;
};

// Convert filter option type to a specific type
export function mapFilterOptionType(type: string) {
  if (!type) return '';

  let result = '';
  if (type.indexOf('opt') > -1) {
    result = 'opt';
  } else if (_.startsWith(type, FILTER_TYPE.VARIANTS_RANGE_SLIDER)) {
    const range = type.substring(FILTER_TYPE.VARIANTS_RANGE_SLIDER.length);
    result = `vrs${range}`;
  } else if (_.startsWith(type, FILTER_TYPE.RANGE_SLIDER)) {
    const range = type.substring(FILTER_TYPE.RANGE_SLIDER.length);
    result = `rs${range}`;
  } else {
    const optionType = _.find(FILTER_TREE_DEFAULT_OPTION_TYPES, item => item.value === type);
    if (!_.isEmpty(optionType)) {
      result = optionType.prefix;
    }
  }

  return result || '';
}

/**
 * Build filter option data from data form
 *
 * @param  {Object}  data data from form
 *
 * @return {Object} filterOption data to submit
 */
export const buildFilterOptionValue = (
  data: Object,
  index?: number,
  filterOptions?: Array<Object>
) => {
  const {
    optionType,
    optionLabel,
    isCollapseMobile,
    isCollapsePC,
    displayTooltip,
    tooltip,
    displayAllValuesInUppercaseForm,
    showSearchBoxFilterMobile,
    showSearchBoxFilterPC,
    showMoreType,
    sortManualValues,
    sortType,
    position,
    selectType,
    status,
    valueType,
    locationInventoryStatus
  } = data;

  // Build Filter option id and label
  const label = prettyText(optionLabel);
  let submitFilterOptionId = `pf_${mapFilterOptionType(optionType)}_${slugify(label).replace(
    /-/g,
    '_'
  )}`;
  if (optionType && optionType.indexOf(FILTER_TYPE.OPTION) > -1) {
    submitFilterOptionId = `pf_${optionType}`;
  } else if (optionType === FILTER_TYPE.REVIEW_RATINGS) {
    submitFilterOptionId = `pf_${mapFilterOptionType(optionType)}_${optionType}`;
  } else if (_.startsWith(optionType, 'metafield')) {
    submitFilterOptionId = `pf_${optionType.replace('metafield_', 'm_')}`;
  } else if (
    optionType === FILTER_TYPE.LOCATION &&
    locationInventoryStatus === 'variant_quantity'
  ) {
    // Apply prefix `vl` for variant location
    submitFilterOptionId = `pf_v${mapFilterOptionType(optionType)}_${slugify(label).replace(
      /-/g,
      '_'
    )}`;
  }

  // Find index of filterOptionId
  const findIndex = (filterOptions, id, index) => {
    return _.findIndex(
      filterOptions,
      (filterOption, filterOptionIndex) =>
        filterOption.filterOptionId === id && index !== filterOptionIndex
    );
  };

  // Check existed id
  if (_.isNumber(index)) {
    let submitFilterOptionIdCheck = '';
    let suffix = 0,
      match = -1;
    do {
      submitFilterOptionIdCheck =
        suffix !== 0 ? `${submitFilterOptionId}_${suffix}` : submitFilterOptionId;
      match = findIndex(filterOptions, submitFilterOptionIdCheck, index);
      suffix += 1;
    } while (match !== -1);
    submitFilterOptionId = submitFilterOptionIdCheck;
  }

  let result = {
    // General
    filterType: optionType,
    label,

    // Advance
    isCollapseMobile,
    isCollapsePC,
    tooltip: displayTooltip ? tooltip : null,
    displayAllValuesInUppercaseForm,
    showSearchBoxFilterMobile,
    showSearchBoxFilterPC,
    showMoreType,
    sortManualValues,
    sortType,

    filterOptionId: submitFilterOptionId,
    position,
    selectType,
    status,
    valueType
  };

  // Build value for specific type
  const type =
    optionType && _.startsWith(optionType, FILTER_TYPE.OPTION)
      ? FILTER_TYPE.OPTION
      : optionType && _.startsWith(optionType, FILTER_TYPE.METAFIELD)
      ? FILTER_TYPE.METAFIELD
      : optionType && _.startsWith(optionType, FILTER_TYPE.RANGE_SLIDER)
      ? FILTER_TYPE.RANGE_SLIDER
      : optionType && _.startsWith(optionType, FILTER_TYPE.VARIANTS_RANGE_SLIDER)
      ? FILTER_TYPE.VARIANTS_RANGE_SLIDER
      : optionType;

  switch (type) {
    // Collection option
    case FILTER_TYPE.COLLECTION: {
      result = collectionHelper.buildFilterOptionValues(data, result);
      break;
    }

    // Vendor option
    case FILTER_TYPE.VENDOR: {
      result = vendorHelper.buildFilterOptionValues(data, result);
      break;
    }

    //Product type option
    case FILTER_TYPE.PRODUCT_TYPE: {
      result = productTypeHelper.buildFilterOptionValues(data, result);
      break;
    }

    //Product category option
    case FILTER_TYPE.PRODUCT_CATEGORY: {
      result = productCategoryHelper.buildFilterOptionValues(data, result);
      break;
    }

    // Location option
    case FILTER_TYPE.LOCATION: {
      result = locationHelper.buildFilterOptionValues(data, result);
      break;
    }

    //Tag option
    case FILTER_TYPE.TAG: {
      result = tagHelper.buildFilterOptionValues(data, result);
      break;
    }

    //Multi level tag option
    case FILTER_TYPE.MULTI_LEVEL_TAG: {
      result = multiLevelTagHelper.buildFilterOptionValues(data, result);
      break;
    }

    //Price option
    case FILTER_TYPE.PRICE: {
      result = priceHelper.buildFilterOptionValues(data, result);
      break;
    }

    //Variant's price  option
    case FILTER_TYPE.VARIANTS_PRICE: {
      result = variantsPriceHelper.buildFilterOptionValues(data, result);
      break;
    }

    // Percent sale
    case FILTER_TYPE.PERCENT_SALE: {
      result = percentSaleHelper.buildFilterOptionValues(data, result);
      break;
    }

    // Review ratings
    case FILTER_TYPE.REVIEW_RATINGS: {
      result = reviewRatingsHelper.buildFilterOptionValues(data, result);
      break;
    }

    // Custom option
    case FILTER_TYPE.OPTION: {
      result = optionHelper.buildFilterOptionValues(data, result);
      break;
    }

    // Metafield option
    case FILTER_TYPE.METAFIELD: {
      result = metafieldHelper.buildFilterOptionValues(data, result);
      break;
    }

    // Stock status
    case FILTER_TYPE.STOCK_STATUS:
      result = stockStatusHelper.buildFilterOptionValues(data, result);
      break;

    // Weight
    case FILTER_TYPE.WEIGHT:
      result = weightHelper.buildFilterOptionValues(data, result);
      break;

    // Range slider
    case FILTER_TYPE.RANGE_SLIDER:
      result = rangeSliderHelper.buildFilterOptionValues(data, result);
      break;

    // Variants Range Slider
    case FILTER_TYPE.VARIANTS_RANGE_SLIDER:
      result = variantsRangeSliderHelper.buildFilterOptionValues(data, result);
      break;

    default:
      break;
  }

  return result;
};

/**
 * Build list of filter option type for select
 *
 * @param  {Object}  options    list option type from tenant
 *
 * @return {Object}  optionType list option type for select
 */
export const buildOptionTypes = (
  options: Object,
  metafields: Object,
  customRangeSliders: Object,
  locations: Object,
  locale: string,
  i18n: Object
) => {
  const defaultType = {
    value: '',
    label: i18n.translate('AdminHTML.FilterTree.Helper.buildOptionTypes_DefaultType')
  };
  let optionTypes = _.cloneDeep(FILTER_TREE_DEFAULT_OPTION_TYPES).map(function (type) {
    return {
      ...type,
      label: i18n.translate(type.label)
    };
  });

  optionTypes.unshift(defaultType);

  const currentState = store.getState();
  let multiLevelTagCollection = _.get(
    currentState,
    `oauth.tenantApp.adminAppFeatures.multiLevelTagCollection`,
    false
  );
  const metafieldOptions = _.get(currentState, `oauth.tenantApp.parseParams.metafields`, []);

  // Remove multi-level tags
  if (!multiLevelTagCollection) {
    _.remove(optionTypes, { value: 'multi_level_tag' });
  }

  // Remove option location if there are only 1 location
  if (locations.length < 2) {
    _.remove(optionTypes, { value: 'location' });
  }

  // Add range slider
  if (!_.isEmpty(customRangeSliders)) {
    customRangeSliders.map(item => {
      optionTypes.push({
        value: `range_slider${item}`,
        label: i18n.translate(
          'AdminHTML.FilterTree.Helper.buildOptionTypes_CustomRangeSlidersWithItem',
          {
            nt_index: item
          }
        ),
        prefix: `rs${item}`,
        multiple: false
      });
      return '';
    });
  } else {
    // Add rang slider with empty content
    optionTypes.push({
      value: `no_range_slider`,
      label: i18n.translate('AdminHTML.FilterTree.Helper.buildOptionTypes_CustomRangeSlidersEmpty'),
      multiple: false,
      prefix: 'noRangeSlider'
    });
  }

  let numberOfVariantsRangeSliders = _.get(
    currentState,
    'oauth.tenantApp.numberOfVariantsRangeSliders',
    1
  );
  // Add variants range slider
  for (let i = 1; i <= numberOfVariantsRangeSliders; i++) {
    optionTypes.push({
      value: `variants_range_slider${i}`,
      label: i18n.translate(
        'AdminHTML.FilterTree.Helper.buildOptionTypes_CustomRangeSlidersVariants',
        {
          nt_index: i
        }
      ),
      prefix: `vrs${i}`,
      multiple: false
    });
  }

  if (options && options.length > 0) {
    options.map(item => {
      if (item) {
        optionTypes.splice(3, 0, {
          value: `opt_${item.name}`,
          label: capitalizeFirstLetter(item.originalName, locale),
          multiple: false,
          prefix: 'opt'
        });
      }
      return '';
    });
  }

  // Add metafields type
  let metafieldOptionCount = 0;
  if (_.isArray(metafields)) {
    if (metafields.length > 0) {
      metafields.forEach(item => {
        const split = item.split('::');
        const namespace = split[1];
        const key = split[2];

        const option = _.find(metafieldOptions, {
          namespace,
          key,
          type: 'metafield_custom_single_metafield'
        });
        const appliesToFilterOption = _.get(option, 'appliesTo.filterOption', false);

        if (appliesToFilterOption) {
          metafieldOptionCount++;
          optionTypes.push({
            value: `metafield_::${namespace}::${key}`,
            label: i18n.translate('AdminHTML.FilterTree.Helper.buildOptionTypes_MetafieldType', {
              nt_namespace: namespace,
              nt_key: key
            }),
            multiple: false,
            prefix: 'metafield'
          });
        }
      });
    }
  }

  // Ensure there are metafield option
  if (!metafieldOptionCount) {
    metafieldOptionCount++;
    // Add metafield with empty content
    optionTypes.push({
      value: `no_metafield`,
      label: i18n.translate('AdminHTML.FilterTree.Common.metafield'),
      multiple: false,
      prefix: 'nometafield'
    });
  }

  return optionTypes;
};

/**
 * Validate option form data before save to filter tree
 *
 * @param  {Object}   data data to check validate
 * @param  {number}   index index of option in filter tree options
 * @param  {Object}   filterOptions filter option list of filter tree
 * @param  {I18n}   i18n
 *
 * @return {boolean/error} true if well validated, throw error if have
 */
export const validateSaveOption = (
  data: Object,
  index: number,
  filterOptions: Object,
  i18n: Object
) => {
  // Check the option type
  if (!data.optionType) {
    throw new SubmissionError({
      optionType: i18n.translate('AdminHTML.FilterTree.Helper.validateSaveOption_SubmissionError1')
    });
  }
  // Check the label is existed
  if (data.optionLabel) {
    const result = _.find(filterOptions, (item, i) => {
      return item.label === data.optionLabel && i !== index * 1;
    });

    if (result) {
      throw new SubmissionError({
        optionLabel: i18n.translate(
          'AdminHTML.FilterTree.Helper.validateSaveOption_SubmissionError2'
        )
      });
    }
  }

  const type =
    data.optionType && _.startsWith(data.optionType, FILTER_TYPE.OPTION)
      ? FILTER_TYPE.OPTION
      : data.optionType && _.startsWith(data.optionType, FILTER_TYPE.METAFIELD)
      ? FILTER_TYPE.METAFIELD
      : data.optionType;

  switch (type) {
    // Collection option
    case FILTER_TYPE.COLLECTION: {
      collectionHelper.validateFilterOption(data, i18n);
      break;
    }

    // Vendor
    case FILTER_TYPE.VENDOR: {
      vendorHelper.validateFilterOption(data, i18n);
      break;
    }

    // Product type
    case FILTER_TYPE.PRODUCT_TYPE: {
      productTypeHelper.validateFilterOption(data, i18n);
      break;
    }

    // Product category
    case FILTER_TYPE.PRODUCT_CATEGORY: {
      productCategoryHelper.validateFilterOption(data, i18n);
      break;
    }

    // Location
    case FILTER_TYPE.LOCATION: {
      locationHelper.validateFilterOption(data, i18n);
      break;
    }

    // Tag
    case FILTER_TYPE.TAG: {
      tagHelper.validateFilterOption(data, i18n);
      break;
    }

    // Multi-level tag
    case FILTER_TYPE.MULTI_LEVEL_TAG: {
      multiLevelTagHelper.validateFilterOption(data);
      break;
    }

    // Custom option
    case FILTER_TYPE.OPTION: {
      optionHelper.validateFilterOption(data, i18n);
      break;
    }

    // Metafield option
    case FILTER_TYPE.METAFIELD: {
      metafieldHelper.validateFilterOption(data, i18n);
      break;
    }

    default:
      break;
  }

  return true;
};

/**
 * Generate faked products for filter option preview
 * @param  {object} filterOption
 *
 * @return {object}              updated filter option includes products
 */
export const generateFakedData = (filterOption: Object) => {
  const { filterType } = filterOption;

  if (filterType === 'multi_level_tag') {
    return filterOption;
  }

  const generate = {
    collection: collectionHelper.fakeProductValues,
    vendor: vendorHelper.fakeProductValues,
    product_type: productTypeHelper.fakeProductValues,
    product_category: productCategoryHelper.fakeProductValues,
    location: locationHelper.fakeProductValues,
    tag: tagHelper.fakeProductValues,
    price: priceHelper.fakeProductValues,
    variants_price: variantsPriceHelper.fakeProductValues,
    percent_sale: percentSaleHelper.fakeProductValues,
    opt_: optionHelper.fakeProductValues,
    metafield_: metafieldHelper.fakeProductValues,
    review_ratings: reviewRatingsHelper.fakeProductValues,
    stock: stockStatusHelper.fakeProductValues,
    weight: weightHelper.fakeProductValues,
    range_slider: rangeSliderHelper.fakeProductValues,
    variants_range_slider: variantsRangeSliderHelper.fakeProductValues
  };

  const updatedFilterOption = _.cloneDeep(filterOption);

  const type =
    filterType && _.startsWith(filterType, FILTER_TYPE.OPTION)
      ? FILTER_TYPE.OPTION
      : filterType && _.startsWith(filterType, FILTER_TYPE.METAFIELD)
      ? FILTER_TYPE.METAFIELD
      : filterType && _.startsWith(filterType, FILTER_TYPE.RANGE_SLIDER)
      ? FILTER_TYPE.RANGE_SLIDER
      : filterType && _.startsWith(filterType, FILTER_TYPE.VARIANTS_RANGE_SLIDER)
      ? FILTER_TYPE.VARIANTS_RANGE_SLIDER
      : filterType;

  updatedFilterOption.values = type && generate[type] ? generate[type](filterOption) : [];
  return updatedFilterOption;
};

/**
 * Fake product data for Filter option which data from filter data
 * @param  {object} filterOption
 *
 * @return {object}              updated filter option includes products
 */
export const fakedData = (filterOption: Object, type: string) => {
  const { valueType } = filterOption;

  let values = [];
  if (valueType === 'specific') {
    let manualValues = _.get(filterOption, 'manualValues', []);

    // Limit product item
    manualValues = _.slice(manualValues, 0, FAKED_PRODUCT_COUNT);

    values = _.map(manualValues, item => {
      return {
        doc_count: Math.floor(Math.random() * 100) + 1,
        key: item
      };
    });
  } else {
    const currentState = store.getState();
    const { filterType, excludedValues, prefix } = filterOption;

    // Get filter data by type
    let items = [];
    const filterDataType = _.get(currentState.filterData, 'filterDataType', '');

    // Get values from previous fetching filter data type
    if (type === filterDataType) {
      items = _.cloneDeep(_.get(currentState.filterData, 'filterDataByTypeFirstPage', []));
    } else {
      items = _.cloneDeep(_.get(currentState.filterData, `filterData.${type}`, []));
    }

    // Filter prefix
    if (prefix) {
      items = _.filter(items, item => _.startsWith(item.value, prefix));
    }

    // Remove excluded values
    if (!_.isEmpty(excludedValues)) {
      _.map(excludedValues, item => _.remove(items, i => i.value === item));
    }

    // Remove non fulfillment items
    if (filterType === 'location' && valueType === 'available_sale_online') {
      _.remove(items, i => !i.fulfillsOnlineOrders);
    }

    // Limit product item
    const subItems = _.slice(items, 0, FAKED_PRODUCT_COUNT);

    values = _.map(subItems, item => {
      const { value } = item;

      return {
        doc_count: Math.floor(Math.random() * 100) + 1,
        key: value
      };
    });
  }
  return values;
};
